import { useState } from 'react'

import Price from '@models/commerce/Price'
import { useAPIError } from '@providers/APIErrorProvider'
import CommerceService from '@services/CommerceService'
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js'

import useAsyncEffect from './useAsyncEffect'

// params used as <input> name values
// and expected by Stripe token request
export enum StripeTokenParams {
  NAME = 'name',
  COUNTRY = 'address_country',
  ADDRESS1 = 'address_line1',
  ADDRESS2 = 'address_line2',
  CITY = 'address_city',
  STATE = 'address_state',
  ZIP = 'address_zip',
}

export const useClientSecret = (price: Price) => {
  const [clientSecret, setStripeClientSecret] = useState<string | undefined>()
  useAsyncEffect(async () => {
    const total = price.getTotal()
    if (total > 0) {
      setStripeClientSecret(await CommerceService.stripeClientSecret(total, price.currency))
    }
  }, [price.priceInCents])
  return clientSecret
}

// This hook is used to create a Stripe Token
const useCustomerPayment = () => {
  const stripe = useStripe()
  const elements = useElements()
  const { addError } = useAPIError()

  const createTokenFromPaymentForm = async (formData) => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      return
    }
    // Stripe token API uses slightly different param names for the address field
    const params = {
      [StripeTokenParams.NAME]: formData.name,
      [StripeTokenParams.COUNTRY]: formData.country,
      [StripeTokenParams.ADDRESS1]: formData.address1,
      [StripeTokenParams.ADDRESS2]: formData.address2,
      [StripeTokenParams.CITY]: formData.city,
      [StripeTokenParams.STATE]: formData.state,
      [StripeTokenParams.ZIP]: formData.postal_code,
    }

    const cardElement = elements.getElement(CardNumberElement)

    if (cardElement) {
      const { token, error } = await stripe.createToken(cardElement, params)

      if (error) {
        addError(error)
        console.error(error)
      }
      return token?.id
    }
  }

  return {
    createTokenFromPaymentForm,
  }
}

export default useCustomerPayment
